// Adverse events section + declare-AE modal with conditional SAE sub-form,
// ported from renderAESection/openAddAEModal/renderSAEForm/submitAddAE
// (source lines 6984-7820). The 24h-pharmacovigilance-notification reminder
// alert is preserved verbatim (source's only enforcement of that rule is a
// soft reminder, not a tracked deadline - the SAE-completeness fields
// themselves ARE hard-enforced, but server-side now, in the safety service).
import { aeApi } from "./api-client.js";
import { escapeHtml, fmtDateOnly, openModal, closeModal, notify, $, $$ } from "./dom.js";
import { AE_CAUSALITY, AE_OUTCOME, AE_TYPE, AE_ACTION_TAKEN, AE_SAE_CRITERIA } from "@woof/shared";

export async function renderAESection(pid: string, writable: boolean, vk: string): Promise<string> {
  const list = await aeApi.list(pid);
  const rows = list.length
    ? list
        .map(
          (ae: any) => `
      <tr>
        <td>${escapeHtml(ae.aeterm)}${ae.aeser ? ' <span class="badge badge-danger">SAE</span>' : ""}</td>
        <td>${ae.aetoxgr ?? "—"}</td>
        <td>${escapeHtml(ae.aerel || "—")}</td>
        <td>${escapeHtml(ae.aeout || "—")}</td>
        <td>${fmtDateOnly(ae.aestdat)}</td>
        <td>${ae.ongoing ? '<span class="badge badge-info">En cours</span>' : ae.aeendat ? fmtDateOnly(ae.aeendat) : "—"}</td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="6" class="muted">Aucun événement indésirable.</td></tr>`;

  const html = `
    <p class="field-hint"><strong>${list.length}</strong> événement${list.length > 1 ? "s" : ""} indésirable${list.length > 1 ? "s" : ""} déclaré${list.length > 1 ? "s" : ""}.</p>
    ${writable ? `<button class="btn btn-primary btn-sm" id="btnAddAE">+ Déclarer un EI</button>` : ""}
    <table class="data-table" style="margin-top:10px">
      <thead><tr><th>Description</th><th>Grade</th><th>Causalité</th><th>Issue</th><th>Début</th><th>Fin / Statut</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  return `<div class="section" data-section-id="ae"><div class="section-header"><h4>⚠️ Événements indésirables</h4></div><div class="section-body">${html}</div></div>`;
}

function saeFormHtml(): string {
  const criteria = AE_SAE_CRITERIA.map((c) => `<label class="cbx"><input type="checkbox" data-sae-criterion="${escapeHtml(c)}"><span>${escapeHtml(c)}</span></label>`).join("");
  return `
    <div class="alert-sae" style="margin-top:14px">
      <h4 style="margin:0 0 8px 0;color:#7f1d1d">⚠️ Formulaire SAE — Événement Indésirable Grave</h4>
      <p style="font-size:13px">Déclaration obligatoire à la pharmacovigilance Findimmune dans les 24h : pharmacovigilance@findimmune.com</p>
      <h5 style="margin:14px 0 6px 0">Critères SAE applicables (cocher)</h5>
      <div class="cbx-stack" style="background:white;padding:10px;border-radius:6px">${criteria}</div>
      <div class="form-grid" style="margin-top:14px">
        <div class="form-group"><label>Date de connaissance du SAE <span class="required-mark">*</span></label><input type="date" id="sae_aware_date"></div>
      </div>
      <div class="form-group"><label>Chronologie détaillée <span class="required-mark">*</span></label><textarea id="sae_chronology" rows="4"></textarea></div>
      <div class="form-group"><label>Mesures de prise en charge</label><textarea id="sae_management" rows="2"></textarea></div>
      <div class="form-grid">
        <div class="form-group"><label>Investigateur déclarant</label><input type="text" id="sae_reporter"></div>
        <div class="form-group"><label>Date de déclaration à la PV</label><input type="date" id="sae_pv_date"></div>
      </div>
    </div>`;
}

function openAddAEModal(pid: string, vk: string, onChange: () => void): void {
  const opt = (values: string[]) => ['<option value="">—</option>', ...values.map((v) => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`)].join("");
  const body = `
    <div class="form-group">
      <label>Description / Verbatim <span class="required-mark">*</span></label>
      <textarea id="ae_verbatim" rows="3" placeholder="Description clinique de l'événement (verbatim de l'investigateur)"></textarea>
      <p class="field-hint">Le codage MedDRA (Preferred Term) sera réalisé ultérieurement par le sponsor.</p>
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Grade CTCAE</label><select id="ae_grade">${['1', '2', '3', '4', '5'].map((g) => `<option value="${g}">Grade ${g}</option>`).join("")}</select></div>
      <div class="form-group"><label>Type</label><select id="ae_type">${opt(AE_TYPE)}</select></div>
      <div class="form-group"><label>Causalité</label><select id="ae_caus">${opt(AE_CAUSALITY)}</select></div>
      <div class="form-group"><label>Issue</label><select id="ae_outcome">${opt(AE_OUTCOME)}</select></div>
      <div class="form-group"><label>Action sur traitement F1901</label><select id="ae_action">${opt(AE_ACTION_TAKEN)}</select></div>
      <div class="form-group"><label>Date début <span class="required-mark">*</span></label><input type="date" id="ae_start"></div>
      <div class="form-group"><label>Date fin (si résolu)</label><input type="date" id="ae_end"></div>
    </div>
    <label class="cbx" style="margin:12px 0;padding:10px 14px;background:#fef2f2;border:2px solid #fca5a5;border-radius:8px">
      <input type="checkbox" id="ae_serious"><span><strong>SAE — Événement Indésirable Grave</strong></span>
    </label>
    <div id="ae_sae_form" style="display:none">${saeFormHtml()}</div>`;
  openModal("Déclarer un Événement Indésirable", body, `<button class="btn btn-secondary" id="aeCancel">Annuler</button><button class="btn btn-primary" id="aeSave">Déclarer l'EI</button>`, true);

  $("#ae_serious")!.addEventListener("change", (e) => {
    ($("#ae_sae_form") as HTMLElement).style.display = (e.target as HTMLInputElement).checked ? "block" : "none";
  });
  $("#aeCancel")!.addEventListener("click", closeModal);
  $("#aeSave")!.addEventListener("click", async () => {
    const aeterm = ($("#ae_verbatim") as HTMLTextAreaElement).value.trim();
    if (!aeterm) return notify("error", "Description / Verbatim obligatoire");
    const aestdat = ($("#ae_start") as HTMLInputElement).value;
    if (!aestdat) return notify("error", "Date début obligatoire");
    const aeser = ($("#ae_serious") as HTMLInputElement).checked;

    let sae: any;
    if (aeser) {
      const criteria = $$<HTMLInputElement>("[data-sae-criterion]:checked").map((el) => el.dataset.saeCriterion!);
      if (criteria.length === 0) return notify("error", "Au moins un critère SAE doit être coché.");
      const awareDate = ($("#sae_aware_date") as HTMLInputElement).value;
      if (!awareDate) return notify("error", "Date de connaissance du SAE obligatoire.");
      const chronology = ($("#sae_chronology") as HTMLTextAreaElement).value.trim();
      if (!chronology) return notify("error", "Chronologie détaillée obligatoire pour un SAE.");
      sae = {
        criteria,
        awareDate,
        chronology,
        management: ($("#sae_management") as HTMLTextAreaElement).value || undefined,
        reporter: ($("#sae_reporter") as HTMLInputElement).value || undefined,
        pvNotifiedDate: ($("#sae_pv_date") as HTMLInputElement).value || undefined,
      };
    }

    try {
      await aeApi.create({
        patientId: pid,
        aeterm,
        aetoxgr: Number(($("#ae_grade") as HTMLSelectElement).value) || 1,
        type: ($("#ae_type") as HTMLSelectElement).value || "Other",
        aerel: ($("#ae_caus") as HTMLSelectElement).value || "Unclassified",
        aeout: ($("#ae_outcome") as HTMLSelectElement).value || "Ongoing",
        aeacn: ($("#ae_action") as HTMLSelectElement).value || "Aucune",
        aestdat,
        aeendat: ($("#ae_end") as HTMLInputElement).value || undefined,
        aeser,
        visitKey: vk,
        sae,
      });
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur");
      return;
    }
    closeModal();
    if (aeser) {
      setTimeout(() => {
        notify("warning", "SAE déclaré. N'oubliez pas de transmettre la déclaration à la pharmacovigilance Findimmune dans les 24 heures :\npharmacovigilance@findimmune.com");
      }, 200);
    }
    onChange();
  });
}

export function attachAEHandlers(root: ParentNode, pid: string, vk: string, onChange: () => void): void {
  $("#btnAddAE", root)?.addEventListener("click", () => openAddAEModal(pid, vk, onChange));
}
